import { memo, useCallback, useState } from 'react';
import { Group, Key } from 'react-aria-components';

import {
  Button,
  Flex,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Popover,
  Select,
  SelectValue,
  Separator,
  Switch,
  Text,
} from '@/components/core';
import { AddIcon, MinusIcon } from '@/components/icons';
import { findField, FormatterValidatorHeader } from '@/components/ui';
import { useFormatterMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';

import { FormatterTest } from './FormatterTest';

const FLOAT_NUMERIC_TYPE = 'float';
const INTEGER_NUMERIC_TYPE = 'integer';

const NUMERIC_TYPE_OPTIONS = [
  {
    id: FLOAT_NUMERIC_TYPE,
    label: 'Float',
    description: 'Convert to a decimal number',
  },
  {
    id: INTEGER_NUMERIC_TYPE,
    label: 'Integer',
    description: 'Convert to integer',
  },
];

export type NumericFormatterProps = {
  fieldId: string;
  formatterId: string;
};

const INumericFormatter = ({ fieldId, formatterId }: NumericFormatterProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useFormatterMutation({ fieldId, formatterId, parentId: field?.parentId });
  const formatter = field?.formatters?.find((f) => f.id === formatterId);

  const numDecimals = formatter?.config?.decimals ? Number(formatter?.config?.decimals) : undefined;
  const [showLimitDecimals, setShowLimitDecimals] = useState(!!numDecimals);

  const setNumericType = useCallback((key: Key) => mutate((f) => (f.config.type = key.toString())), [mutate]);

  const onChangePrecision = useCallback(
    (n: number | undefined) => {
      const v = Number.isFinite(n) ? n : undefined;
      mutate((f) => ((f.config.decimals as number | undefined) = v));
    },
    [mutate]
  );

  const numericType = formatter?.config.type ?? FLOAT_NUMERIC_TYPE;

  const toggleLimitDecimals = useCallback(
    (isSelected: boolean) => {
      if (!isSelected) {
        onChangePrecision(undefined);
      }
      setShowLimitDecimals(isSelected);
    },
    [onChangePrecision]
  );

  return (
    <Flex col className="gap-4 p-4">
      <FormatterValidatorHeader formatter={formatter} />

      <Separator orientation="horizontal" />

      <Select onSelectionChange={setNumericType} defaultSelectedKey={FLOAT_NUMERIC_TYPE}>
        <Label>Numeric type</Label>
        <Button variant="secondary">
          <SelectValue />
        </Button>

        <Popover>
          <ListBox>
            {NUMERIC_TYPE_OPTIONS.map((t) => (
              <ListBoxItem id={t.id}>
                <Text slot="label" className="font-bold">
                  {t.label}
                </Text>
                <Text slot="description">{t.description}</Text>
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </Select>

      <div className="flex grow flex-col gap-4">
        {numericType === FLOAT_NUMERIC_TYPE && (
          <div className="flex w-full items-center justify-between">
            <div className="grow">
              <Switch onChange={toggleLimitDecimals} defaultSelected={!!numDecimals}>
                <Label>Limit number of decimals</Label>
              </Switch>
            </div>

            <div>
              <NumberField
                minValue={0}
                onChange={onChangePrecision}
                className={showLimitDecimals ? '' : 'invisible'}
                defaultValue={numDecimals}
              >
                <Group className="gap-xs flex">
                  <Button slot="decrement" variant="plain">
                    <MinusIcon />
                  </Button>

                  <div className="w-8">
                    <Input placeholder="2" onInput={(e) => onChangePrecision(Number(e.currentTarget.value))} />
                  </div>

                  <Button slot="increment" variant="plain">
                    <AddIcon />
                  </Button>
                </Group>
              </NumberField>
            </div>
          </div>
        )}
      </div>

      <FormatterTest fieldId={fieldId} formatterId={formatterId} />
    </Flex>
  );
};

export const NumericFormatter = memo(INumericFormatter) as typeof INumericFormatter;
